/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Layers,
  PlusCircle,
  Search,
  Edit2,
  Trash2,
  Copy,
  Filter,
  X,
  Building2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { subAccountService } from '../services/subAccountService';
import { accountService } from '../services/accountService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import Modal from '../components/Modal';
import { usePermissions } from '../context/PermissionContext';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 10;

const INITIAL_FILTERS = {
  search: '',
  accountUid: '',
  status: 'All',
};

// Case-insensitive A→Z sort of filter options by name.
const sortByName = (list) =>
  [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const SubAccounts = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user } = useAuth();

  const [subAccounts, setSubAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [serviceRequestTypes, setServiceRequestTypes] = useState([]);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    totalPages: 0
  });

  const showAccountFilter = !user?.accountUID;
  const showAccountColumn = !user?.accountUID && !filters.accountUid;
  const canShowActions = can.sub_accounts.create() || can.sub_accounts.update() || can.sub_accounts.delete();

  // Load service request types
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ limit: 100 });
        setServiceRequestTypes(data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadTypes();
  }, []);

  // Fetch accounts if user is platform admin
  useEffect(() => {
    if (!user?.accountUID) {
      const fetchAccounts = async () => {
        try {
          const { data } = await accountService.getAccounts({ limit: 100 });
          setAccounts(sortByName(data.data || data || []));
        } catch {
          toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
        }
      };
      fetchAccounts();
    }
  }, [user?.accountUID]);

  // Debounce filter changes
  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const requestIdRef = useRef(0);
  const fetchSubAccounts = useCallback(async (pageOverride) => {
    const currentRequestId = ++requestIdRef.current;
    const currentPagination = paginationRef.current;
    const accountId = debouncedFilters.accountUid || user?.accountUID || null;

    try {
      setLoading(true);
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
        search: debouncedFilters.search || undefined,
        status: debouncedFilters.status && debouncedFilters.status !== 'All' ? debouncedFilters.status : undefined,
      };

      const { data } = await subAccountService.getSubAccounts(accountId, params);
      if (currentRequestId !== requestIdRef.current) return;

      const subAccountsList = data.data || [];
      const paginationData = data.pagination || {};
      const total = paginationData.total ?? data.total ?? subAccountsList.length;
      const limit = paginationData.limit ?? data.limit ?? currentPagination.limit;
      const totalPages = paginationData.totalPages ?? data.totalPages ?? Math.ceil(total / limit);

      setSubAccounts(subAccountsList);
      setPagination({
        page: paginationData.page ?? data.page ?? (pageOverride ?? currentPagination.page),
        limit,
        total,
        totalPages
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedFilters, user?.accountUID]);

  const isFirstFilterFetchRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterFetchRender.current) {
      isFirstFilterFetchRender.current = false;
      return;
    }
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [debouncedFilters, user?.accountUID]);

  useEffect(() => {
    fetchSubAccounts(pagination.page);
  }, [pagination.page, fetchSubAccounts]);

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
  };

  const handleDeleteConfirm = async () => {
    const accountId = selectedSubAccount?.accountId || filters.accountUid || user?.accountUID;
    if (!accountId || !selectedSubAccount) { return; }

    try {
      setSubmitting(true);
      await subAccountService.deleteSubAccount(accountId, selectedSubAccount.id);
      toastService.success(`${SUB_ACCOUNT_LABEL.singular} deleted successfully`);
      setShowDeleteModal(false);
      setSelectedSubAccount(null);
      fetchSubAccounts(pagination.page);
    } catch (err) {
      toastService.error(err.response?.data?.message || `Failed to delete ${SUB_ACCOUNT_LABEL.singularLower}`);
    } finally {
      setSubmitting(false);
    }
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.accountUid ||
    filters.status !== 'All'
  );

  const filterColSpans = useMemo(() => {
    if (showAccountFilter) {
      return {
        search: 'col-span-12 sm:col-span-6 lg:col-span-6',
        account: 'col-span-12 sm:col-span-3 lg:col-span-3',
        status: 'col-span-12 sm:col-span-3 lg:col-span-3',
      };
    }
    return {
      search: 'col-span-12 sm:col-span-8 lg:col-span-9',
      status: 'col-span-12 sm:col-span-4 lg:col-span-3',
    };
  }, [showAccountFilter]);

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {SUB_ACCOUNT_LABEL.plural}
            </h1>
            <p className="text-xs text-base-content/50">
              Manage {SUB_ACCOUNT_LABEL.pluralLower}, configure branding, and assign {SERVICE_REQUEST_LABEL.singularLower} types.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {can.sub_accounts.create() && (
              <button
                onClick={() => navigate('/sub-accounts/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New {SUB_ACCOUNT_LABEL.singular}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        {/* Main Search and Filter Grid */}
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={`relative ${filterColSpans.search}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder={`Search ${SUB_ACCOUNT_LABEL.pluralLower} by name, subdomain or external ID...`}
              aria-label={`Search ${SUB_ACCOUNT_LABEL.pluralLower} by name, subdomain or external ID`}
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Account Filter (Platform Scope Only) */}
          {showAccountFilter && (
            <div className={filterColSpans.account}>
              <select
                aria-label={`Filter by ${ACCOUNT_LABEL.singularLower}`}
                className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={filters.accountUid}
                onChange={(e) => setFilters(f => ({ ...f, accountUid: e.target.value }))}
              >
                <option value="">All {ACCOUNT_LABEL.plural}</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          <div className={filterColSpans.status}>
            <select
              aria-label="Filter by status"
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="All">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, search: '' }))} className="hover:text-error" aria-label="Clear search filter">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.accountUid && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Building2 size={10} className="text-primary" />
                  <span>{ACCOUNT_LABEL.singular}: {accounts.find(a => a.id === filters.accountUid)?.name || filters.accountUid}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, accountUid: '' }))} className="hover:text-error" aria-label={`Clear ${ACCOUNT_LABEL.singularLower} filter`}>
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Status: {filters.status === 'active' ? 'Active' : 'Inactive'}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, status: 'All' }))} className="hover:text-error" aria-label="Clear status filter">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : subAccounts.length === 0 ? (
          <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
              <Layers size={28} />
            </div>
            <div>
              <h3 className="text-base font-black text-base-content">
                {hasActiveFilters ? `No matching ${SUB_ACCOUNT_LABEL.pluralLower}` : `No ${SUB_ACCOUNT_LABEL.pluralLower} yet`}
              </h3>
              <p className="text-xs text-base-content/40 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search criteria or clearing active filters.'
                  : `Get started by creating your first ${SUB_ACCOUNT_LABEL.singularLower}.`}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
              >
                Clear all filters
              </button>
            ) : can.sub_accounts.create() ? (
              <button
                type="button"
                onClick={() => navigate('/sub-accounts/new')}
                className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
              >
                <PlusCircle size={14} />
                <span>Create {SUB_ACCOUNT_LABEL.singular}</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  <th className="py-3.5 px-5">{SUB_ACCOUNT_LABEL.singular}</th>
                  <th className="py-3.5 px-4">External {SUB_ACCOUNT_LABEL.singular} ID</th>
                  {showAccountColumn && <th className="py-3.5 px-4">{ACCOUNT_LABEL.singular}</th>}
                  <th className="py-3.5 px-4">{SERVICE_REQUEST_LABEL.singular} Types</th>
                  <th className="py-3.5 px-4">Created Date</th>
                  {canShowActions && <th className="py-3.5 px-5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-content/5 text-xs">
                {subAccounts.map((sub) => {
                  const srtDisplay = sub.serviceRequestTypes?.length > 0 
                    ? sub.serviceRequestTypes.map(t => t.name).join(', ') 
                    : (sub.serviceRequestTypeIds?.length > 0
                        ? sub.serviceRequestTypeIds.map(id => serviceRequestTypes.find(t => t.uid === id)?.name).filter(Boolean).join(', ') || '—'
                        : '—');

                  return (
                    <tr key={sub.id} className="hover:bg-base-200/40 transition-colors group">
                      {/* Sub-Account Name */}
                      <td className="py-3.5 px-5 font-bold">
                        <span className="font-bold text-base-content tracking-tight">
                          {sub.name}
                        </span>
                      </td>

                      {/* External Sub-Account ID */}
                      <td className="py-3.5 px-4">
                        {sub.externalSubAccountId ? (
                          <code className="text-xs font-mono font-bold bg-base-200 px-2 py-0.5 rounded-lg text-base-content/70">
                            {sub.externalSubAccountId}
                          </code>
                        ) : (
                          <span className="text-base-content/40">—</span>
                        )}
                      </td>

                      {/* Account */}
                      {showAccountColumn && (
                        <td className="py-3.5 px-4 text-base-content/60 font-medium">
                          {sub.accountName || sub.account?.name || '—'}
                        </td>
                      )}

                      {/* Service Request Types */}
                      <td className="py-3.5 px-4">
                        <span className="font-semibold text-base-content/70">
                          {srtDisplay}
                        </span>
                      </td>

                      {/* Created Date */}
                      <td className="py-3.5 px-4 text-base-content/70 font-medium whitespace-nowrap">
                        {sub.createdAt ? (
                          <div
                            className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                            data-tip={formatDateTime(sub.createdAt)}
                          >
                            <span className="cursor-default hover:text-base-content transition-colors font-mono">
                              {formatDate(sub.createdAt)}
                            </span>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>

                      {/* Actions */}
                      {canShowActions && (
                        <td className="py-3.5 px-5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {can.sub_accounts.create() && user?.scope === 'platform' && (
                              <div className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Clone">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/sub-accounts/${sub.id}/clone`, { state: { sub } })}
                                  className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                  aria-label={`Clone ${SUB_ACCOUNT_LABEL.singular}`}
                                >
                                  <Copy size={15} />
                                </button>
                              </div>
                            )}

                            {can.sub_accounts.update() && (
                              <div className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Edit">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/sub-accounts/${sub.id}/edit`, { state: { sub } })}
                                  className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                  aria-label={`Edit ${SUB_ACCOUNT_LABEL.singular}`}
                                >
                                  <Edit2 size={15} />
                                </button>
                              </div>
                            )}

                            {can.sub_accounts.delete() && (
                              <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedSubAccount(sub);
                                    setShowDeleteModal(true);
                                  }}
                                  className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                  aria-label={`Delete ${SUB_ACCOUNT_LABEL.singular}`}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Table Footer with Pagination */}
        {!loading && subAccounts.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-base-content/60 font-medium">
              <span className="font-semibold text-base-content">
                {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-base-content">{pagination.total}</span> records
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-base-content/50 font-medium">
                  Page <span className="font-semibold text-base-content">{pagination.page}</span> /{' '}
                  <span className="font-semibold text-base-content">{pagination.totalPages}</span>
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pagination.page === 1 || loading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Previous Page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Next Page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={`Delete ${SUB_ACCOUNT_LABEL.singular}`}
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete {SUB_ACCOUNT_LABEL.singularLower}{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {selectedSubAccount?.name}
            </strong>?
            {selectedSubAccount?.accountName && (
              <> This will remove this business unit from <strong className="text-primary">{selectedSubAccount.accountName}</strong>.</>
            )}
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This action cannot be undone. All associated configurations will be removed.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowDeleteModal(false)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {submitting ? 'Deleting...' : `Delete ${SUB_ACCOUNT_LABEL.singular}`}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SubAccounts;
